import * as Sentry from '@sentry/browser';
import _ from 'underscore';

import { I9nSchemaBySystem } from '@biteinc/common';
import {
  AlertType,
  CanaryTier,
  CardSchemeId,
  CardSchemeIdHelper,
  CountryCode,
  CountryCodeHelper,
  CouponCodeTransform,
  CouponProvider,
  CouponProviderHelper,
  CustomerIdentifier,
  CustomerIdentifierHelper,
  CustomerIdentifierInputMethod,
  CustomerIdentifierInputMethodHelper,
  FulfillmentMethod,
  FulfillmentMethodHelper,
  IntegrationSystem,
  LocationMultiPaymentSupport,
  LocationMultiVendorSupport,
  LocationPaymentType,
  LocationPresetTipType,
  LocationState,
  LocationStateHelper,
  LocationTipsLevel,
  LocationTipsLevelHelper,
  MenuItemCategory,
  MenuItemCategoryHelper,
  ModelType,
  OrderChannel,
  OrderChannelHelper,
  PromotionThresholdType,
  QsrAutomationsMode,
  Timezone,
  TimezoneHelper,
  WayfinderHotspotType,
  WayfinderHotspotVariant,
  WayfinderHotspotVariantHelper,
  WayfinderLinkType,
} from '@biteinc/enums';
import { StringHelper, Time } from '@biteinc/helpers';
import { diningOptionSchema, locationSchema } from '@biteinc/schemas';

import { CryptHelper } from '../helpers/crypt_helper';

app.Location = app.AbstractModel.extend({
  ModelName: 'location',
  Schema: locationSchema,
  Type: ModelType.Location,

  initialize() {
    this.Schema.fields.wentLiveAt.useSiteTimezone = true;
    app.AbstractModel.prototype.initialize.apply(this, arguments);
  },

  canBeDestroyed() {
    return false;
  },

  nameWithChannel() {
    return `${this.get('name')} (${this.get('channelName')})`;
  },

  debugNameWithChannel() {
    return `${this.debugName()} (${this.get('channelName')})`;
  },

  debugName() {
    const prefix = this.get('orgName').toLowerCase();
    let locationName = this.get('name');
    if (locationName.toLowerCase().indexOf(prefix) === 0) {
      locationName = locationName.substr(prefix.length).trim();
    }
    if (locationName.length) {
      return `${this.get('orgName')} ${locationName}`;
    }
    return this.get('orgName');
  },

  isCanary() {
    return this.get('vitrineCanaryTier') !== null;
  },

  isGcnCanary() {
    return this.get('gcnCanaryTier') !== null;
  },

  isGarconCanary() {
    return this.get('garconCanaryTier') !== null;
  },

  isLab() {
    return !!this.get('isLabLocation');
  },

  isPilot() {
    return !!this.get('isPilotLocation');
  },

  isLive() {
    return this.get('state') === LocationState.Live;
  },

  isChurned() {
    return this.get('state') === LocationState.Churned;
  },

  isBiteOnly() {
    return this.get('state') === LocationState.BiteOnly;
  },

  hasCouponProvider() {
    return this.get('couponProvider') !== CouponProvider.None;
  },

  bureauUrl() {
    return `/${this.get('orgNormalizedName')}/${this.get('normalizedName')}/${this.get(
      'channelNormalizedName',
    )}`;
  },

  getStripeEmail(vendorId) {
    if (this.get('multiPaymentSupport') === LocationMultiPaymentSupport.Enabled) {
      return `${this.get('orgNormalizedName')}-${vendorId}-${this.get(
        'shortCode',
      )}@store.biteflash.com`;
    }
    return `${this.get('orgNormalizedName')}-${this.get('shortCode')}@store.biteflash.com`;
  },

  getFlashUrl() {
    if (this.get('orgDomain')) {
      return `https://${this.get('orgDomain')}/${this.get('urlSlug')}`;
    }
    return `${app.data.FLASH_PROMO_BASE_URL}/${this.get('urlSlug')}`;
  },

  getFlashUrlsWithSessionToken(externalAuthStrategy, forcedMenuStructureId) {
    /** @type {I9nSchema[]} */
    const storedValueI9nSchemas = this.getStoredValueI9nSchemas();

    const decryptedTokensAndNames = storedValueI9nSchemas.flatMap((storedValueI9nSchema) => {
      switch (storedValueI9nSchema.system) {
        case IntegrationSystem.AtriumCampusCard:
        case IntegrationSystem.AtriumStoredValue:
          // Atrium sandbox certification card; sessions born from this link exercise the
          // BYU-style flow (stored value provided by an external portal's session token).
          return [
            {
              name: `${storedValueI9nSchema.name} sandbox session`,
              decryptedToken: JSON.stringify({
                version: 1,
                cards: [
                  {
                    cardName: 'Atrium Session Card',
                    cardNumber: '006E2BBB',
                  },
                ],
                validAt: Date.now(),
              }),
            },
          ];
        case IntegrationSystem.PinataStoredValue:
          return [
            {
              name: 'legacy v0 unlimited',
              decryptedToken: `${Date.now()}-${Date.now()}v0`,
            },
            {
              name: 'legacy v0 50 cent',
              decryptedToken: `${Date.now()}-${Date.now()}v0balance${50}`,
            },
            {
              name: 'v1',
              decryptedToken: JSON.stringify({
                version: 1,
                cards: [
                  {
                    cardName: '50 Cent Card',
                    cardNumber: `${Date.now()}v1balance${50}`,
                  },
                  {
                    cardName: '1 Dollar Card',
                    cardNumber: `${Date.now()}v1balance${100}`,
                  },
                  {
                    cardName: 'Unlimited',
                    cardNumber: `${Date.now()}v1`,
                  },
                ],
                validAt: Date.now(),
              }),
            },
          ];
        default:
          return [];
      }
    });

    return decryptedTokensAndNames.map(({ name, decryptedToken }) => {
      const sessionToken = this._encryptToken(decryptedToken, externalAuthStrategy);
      const url = `${this.getFlashUrl()}?sessionToken=${sessionToken}${
        forcedMenuStructureId ? `&forcedMenuStructureId=${forcedMenuStructureId}` : ''
      }`;
      return {
        name,
        url,
      };
    });
  },

  _encryptToken(decryptedToken, authStrategy) {
    const encryptedToken = CryptHelper.encrypt(decryptedToken, authStrategy.get('secretKey'));
    const encodedSessionToken = encodeURIComponent(encryptedToken);
    return encodedSessionToken;
  },

  getFieldCollection(field, subProperty, _includeAllValues, keyModel) {
    const fieldName = (subProperty ? `${subProperty}.` : '') + field;
    switch (fieldName) {
      case 'alertRules.frequencyByAlertType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: AlertType,
          nameGenerator: (alertType) => {
            switch (alertType) {
              case AlertType.KioskDown:
                return 'Kiosk Down or Missing';
              case AlertType.KioskTurnedOffManually:
                return 'Kiosk Turned Off Manually';
              case AlertType.Ordering:
                return 'Ordering';
              case AlertType.SendingOrderItem86d:
                return 'Item 86d Sending Order';
              case AlertType.GenericSendingOrder:
                return 'Generic Sending Order Error';
              case AlertType.SendingOrderTimeout:
                return 'Sending Order Timeout';
              case AlertType.UncommittedTransactions:
                return 'Uncommitted Transactions';
              case AlertType.SendingWebhookError:
                return 'Sending Webhook Error';
              case AlertType.KioskInactive:
                return 'Kiosk Inactive';
              default:
                throw new Error(`unrecognized alertType value: ${alertType}`);
            }
          },
        });
      case 'vitrineCanaryTier':
      case 'gcnCanaryTier':
      case 'garconCanaryTier':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CanaryTier,
          nameGenerator: (canaryTier) => {
            return `Tier ${canaryTier}`;
          },
        });
      case 'freeItemPromotions.menuItemId': {
        const possibleMenuItems = _.filter(app.menuItemList.models, (item) => {
          return item.canBePromotionItem();
        });
        return new app.MenuItemList(possibleMenuItems);
      }
      case 'freeItemPromotions.thresholdType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: PromotionThresholdType,
          nameGenerator: (thresholdType) => {
            switch (thresholdType) {
              case PromotionThresholdType.CartSize:
                return 'Cart Size';
              case PromotionThresholdType.CartValue:
                return 'Cart Value';
              default:
                return 'N/A';
            }
          },
        });
      case 'freeItemPromotions.fulfillmentMethods':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: FulfillmentMethod,
          nameGenerator: (fulfillmentMethod) => {
            return this.getDiningOptionName(fulfillmentMethod);
          },
          values: app.location.getExistingFulfillmentMethods(),
        });
      case 'menuReferenceLocationId':
        return new app.LocationList(
          app.locationList.models.filter((location) => {
            return location.id !== this.id;
          }),
        );
      case 'multiVendorSupport':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationMultiVendorSupport,
          nameGenerator: (multiVendorSupport) => {
            switch (multiVendorSupport) {
              case LocationMultiVendorSupport.Disabled:
                return 'Disabled';
              case LocationMultiVendorSupport.Enabled:
                return 'Enabled';
              default:
                throw new Error(`unrecognized multiVendorSupport value: ${multiVendorSupport}`);
            }
          },
          disclaimerGenerator: (multiVendorSupport) => {
            switch (multiVendorSupport) {
              case LocationMultiVendorSupport.Disabled:
                return '';
              case LocationMultiVendorSupport.Enabled:
                return 'For situations when all POS integrations have different rev centers or point to actually different POSs. Does not allow for shared items.';
              default:
                throw new Error(`unrecognized multiVendorSupport value: ${multiVendorSupport}`);
            }
          },
        });
      case 'multiPaymentSupport':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationMultiPaymentSupport,
          nameGenerator: (multiPaymentSupport) => {
            switch (multiPaymentSupport) {
              case LocationMultiPaymentSupport.Disabled:
                return 'Disabled (All Use a Single Payment Integration)';
              case LocationMultiPaymentSupport.Enabled:
                return 'Enabled (Each Vendor uses a Single Payment Integration)';
              default:
                throw new Error(`unrecognized multiPaymentSupport value: ${multiPaymentSupport}`);
            }
          },
        });
      case 'orderChannel':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: OrderChannel,
          nameGenerator: OrderChannelHelper.name,
        });
      case 'state':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationState,
          nameGenerator: LocationStateHelper.name,
          // We can't change the state of Bite-Only locations from here; the site must have the
          // Bite-Only flag set to false
          values:
            this.get('state') === LocationState.BiteOnly
              ? [LocationState.BiteOnly]
              : [
                  LocationState.Draft,
                  LocationState.Live,
                  LocationState.Churned,
                  LocationState.LabWithLivePos,
                ],
        });
      case 'paymentType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationPaymentType,
          nameGenerator: (paymentType) => {
            switch (paymentType) {
              case LocationPaymentType.CashierOnly:
                return 'Only at a cashier';
              case LocationPaymentType.CreditCard:
                return 'Credit card';
              default:
                throw new Error(`unrecognized paymentType value: ${paymentType}`);
            }
          },
        });
      case 'orgAppearanceId':
        return app.orgAppearanceList;
      case 'countryCode':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CountryCode,
          nameGenerator: (countryCode) => {
            return CountryCodeHelper.name(countryCode);
          },
        });
      case 'timezone':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: Timezone,
          nameGenerator: (timezone) => {
            const name = TimezoneHelper.name(timezone);
            // prepend timezones likes EST/CST with a space so they are sorted to the top
            return name === timezone ? timezone : ` ${name}`;
          },
        });
      case 'tipsLevel':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationTipsLevel,
          nameGenerator: LocationTipsLevelHelper.name,
        });
      case 'defaultTip.type':
      case 'presetTips.type':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: LocationPresetTipType,
          nameGenerator: (type) => {
            if (type === LocationPresetTipType.Fixed) {
              return 'Fixed';
            }
            if (type === LocationPresetTipType.Percentage) {
              return 'Percentage';
            }
            throw new Error(`unrecognized presetTips.type value: ${type}`);
          },
        });
      case 'couponCodeTransform': {
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CouponCodeTransform,
          nameGenerator: (couponCodeTransform) => {
            return StringHelper.toTitleCase(couponCodeTransform.split('-').join(' '));
          },
        });
      }
      case 'couponProvider':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CouponProvider,
          nameGenerator: CouponProviderHelper.name,
        });
      case 'flashDiningOptions.outposts.pickupTimeSlots.timeSlots':
      case 'kioskDiningOptions.outposts.pickupTimeSlots.timeSlots':
        return app.AbstractCollection.createFromEnum({
          schema: _.map(
            Time.weekTimestampsForOneDay(keyModel.futureOrdersTimeIntervals * Time.MINUTE),
            (timestamp) => {
              return {
                _id: timestamp,
                name: Time.utc(timestamp).format('hh:mma'),
              };
            },
          ),
          sort: app.AbstractCollection.SortOptions.AS_IS,
        });
      case 'kioskDiningOptions.customerIdentifierOptions.numberOfDigits':
      case 'flashDiningOptions.customerIdentifierOptions.numberOfDigits': {
        const { min, max } =
          diningOptionSchema.fields.customerIdentifierOptions.fields.numberOfDigits;
        return app.AbstractCollection.createFromIntValues(min, max);
      }
      case 'kioskDiningOptions.customerIdentifierOptions.inputMethod':
      case 'flashDiningOptions.customerIdentifierOptions.inputMethod': {
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CustomerIdentifierInputMethod,
          nameGenerator: CustomerIdentifierInputMethodHelper.name,
          values: CustomerIdentifierHelper.inputMethods(keyModel.customerIdentifier),
        });
      }
      case 'kioskDiningOptions.toGoBagItemId': {
        const possibleBagOptInItems = _.filter(app.menuItemList.models, (item) => {
          const priceOptions = item.get('priceOptions');
          const archivedAt = item.get('archivedAt');

          return priceOptions.length === 1 && !priceOptions[0].addonSetIds?.length && !archivedAt;
        });

        return new app.MenuItemList(possibleBagOptInItems);
      }
      case 'orderLeadTimeRules.menuSectionIds':
        return app.menuSectionList;
      case 'supportedCardSchemes':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: CardSchemeId,
          nameGenerator: CardSchemeIdHelper.name,
        });
      case 'flashDiningOptions.customerIdentifierOptions.skipIfCartOnlyContainsCategories':
      case 'kioskDiningOptions.customerIdentifierOptions.skipIfCartOnlyContainsCategories':
      case 'skipDiningOptionIfCartOnlyContainsCategories':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemCategory,
          nameGenerator: MenuItemCategoryHelper.name,
        });
      case 'wayfinder.hotspots.type':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: WayfinderHotspotType,
          nameGenerator: (type) => {
            switch (type) {
              case WayfinderHotspotType.Interactive:
                return 'Interactive';
              case WayfinderHotspotType.NonInteractive:
                return 'Non-Interactive';
            }
          },
        });
      case 'wayfinder.hotspots.link.type':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: WayfinderLinkType,
          nameGenerator: (type) => {
            switch (type) {
              case WayfinderLinkType.Section:
                return 'Section';
              case WayfinderLinkType.Page:
                return 'Page';
              case WayfinderLinkType.Item:
                return 'Item';
              case WayfinderLinkType.None:
                return 'None';
            }
          },
        });
      case 'wayfinder.hotspots.variant':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: WayfinderHotspotVariant,
          nameGenerator: WayfinderHotspotVariantHelper.name,
        });
      case 'wayfinder.hotspots.link.targetId': {
        return app.AbstractCollection.createFromEnum({
          schema: {
            ...app.menuSectionList.models?.reduce((sectionsById, section) => {
              sectionsById[section.id] = {
                _id: section.id,
                name: `Section: ${section.attributes.name ?? section.attributes.posName ?? section.id}`,
              };
              return sectionsById;
            }, {}),
            ...app.menuStructureList.models?.reduce((pagesById, structure) => {
              structure.attributes.pages?.forEach((page) => {
                pagesById[page._id] = {
                  _id: page._id,
                  name: `Page: ${page.name ?? page._id}`,
                };

                page.pages?.forEach((subPage) => {
                  pagesById[subPage._id] = {
                    _id: subPage._id,
                    name: `SubPage: ${subPage.name ?? subPage._id}`,
                  };
                });
              });
              return pagesById;
            }, {}),
            ...app.menuItemList.models?.reduce((itemsById, item) => {
              itemsById[item.id] = {
                _id: item.id,
                name: `Item: ${item.get('posName') || item.get('name')}`,
              };
              return itemsById;
            }, {}),
          },
        });
      }
      case 'wayfinder.hotspots.kioskIds':
        return app.kioskList;
      default: {
        const fieldSchema = locationSchema.fields[field];
        if (fieldSchema?.type === 'stringEnum') {
          Sentry.captureException(
            new Error(`Unhandled bureau dropdown for location field ${fieldName}`),
          );
          return app.AbstractCollection.createFromTsEnum({
            tsEnum: fieldSchema.enum,
            nameGenerator: (value) => StringHelper.toTitleCase(value.replaceAll('-', ' ')),
          });
        }
        return null;
      }
    }
  },

  displayNameForListFieldElement(field, element, subProperty, keyModel, plainTextOnly) {
    const displayName = plainTextOnly ? element.displayName() : element.displayNameHtml();
    switch (field) {
      case 'menuReferenceLocationId': {
        const location = element;
        return location.nameWithChannel();
      }
    }
    return displayName;
  },

  getListFieldElementAttributesFromModel(_field, element) {
    const attrs = { _id: element.id };
    return attrs;
  },

  getTooltipForListFieldElement(field, element, subProperty /* keyModel */) {
    const fieldName = (subProperty ? `${subProperty}.` : '') + field;
    switch (fieldName) {
      case 'alertRules.frequencyByAlertType': {
        const alertType = element;
        switch (alertType) {
          case AlertType.KioskDown:
            return { text: 'Kiosk is offline or a required peripheral is not operational.' };
          case AlertType.KioskTurnedOffManually:
            return { text: `Kiosk's "turned off" screen is toggled by the staff.` };
          case AlertType.Ordering:
            return { text: 'POS error when validating or sending an order.' };
          case AlertType.SendingOrderItem86d:
            return { text: 'Something in an order became 86d post-payment.' };
          case AlertType.GenericSendingOrder:
            return { text: 'Anything that prevents the order from being sent to the POS.' };
          case AlertType.SendingOrderTimeout:
            return { text: 'The POS is not responding in time to order submissions.' };
          case AlertType.UncommittedTransactions:
            return { text: 'An authorized transaction could not be committed.' };
          case AlertType.SendingWebhookError:
            return { text: 'An outgoing Bite order webhook error.' };
          case AlertType.KioskInactive:
            return { text: 'No orders have been successfully placed in the last hour.' };
          default:
            throw new Error(`unrecognized alertType value: ${alertType}`);
        }
      }
      default:
        return null;
    }
  },

  fieldIsPermanent(field) {
    if (app.AbstractModel.prototype.fieldIsPermanent.apply(this, arguments)) {
      return true;
    }

    const permanentFields = ['allowOfflineMode', 'mode', 'paymentType', 'multiVendorSupport'];
    if (this.isLive() && _.contains(permanentFields, field)) {
      return true;
    }

    if ('paymentType' === field && this.getKioskPaymentI9nSchema()) {
      return true;
    }

    return false;
  },

  syncMenu(callback) {
    app.MultiQueueHelper.makeRequest(`${this.url()}/sync-menu`, null, true, callback);
  },

  deleteMenu(options) {
    const url = `${this.url()}/menu`;
    app.makeRequest('DELETE', url, null, options.success, options.error);
  },

  refreshMenuCache(options) {
    const url = `${this.url()}/refresh-menu-cache`;
    app.makeRequest('POST', url, null, options.success, options.error);
  },

  removeArchive(options) {
    const url = `${this.url()}/archive`;
    app.makeRequest('DELETE', url, null, options.success, options.error);
  },

  hasIntegrationWithSystem(system) {
    return _.any(this.get('integrationById'), (partialI9n) => {
      return partialI9n.system === system;
    });
  },

  getAllDiningOptions() {
    return [...this.get('flashDiningOptions'), ...this.get('kioskDiningOptions')];
  },

  getDiningOption(fulfillmentMethod) {
    return _.findWhere(this.getAllDiningOptions(), { fulfillmentMethod });
  },

  getDiningOptionName(fulfillmentMethod, showDisabledState = false) {
    const diningOption = this.getDiningOption(fulfillmentMethod);
    const diningOptionName = diningOption?.name || FulfillmentMethodHelper.name(fulfillmentMethod);

    // TODO: remove once we break up flash and kiosk
    const channelSuffix =
      this.get('orderChannel') === OrderChannel.Kiosk
        ? ` (${OrderChannelHelper.name(FulfillmentMethodHelper.orderChannel(fulfillmentMethod))})`
        : '';
    const disabledSuffix =
      showDisabledState && diningOption && !diningOption.isEnabled ? ' [Disabled]' : '';

    return `${diningOptionName}${channelSuffix}${disabledSuffix}`;
  },

  getExistingFulfillmentMethods() {
    return _.pluck(this.getAllDiningOptions(), 'fulfillmentMethod');
  },

  getCollectionFromExistingFulfillmentMethods(showDisabledState = false) {
    return app.AbstractCollection.createFromTsEnum({
      tsEnum: FulfillmentMethod,
      nameGenerator: (fulfillmentMethod) => {
        return this.getDiningOptionName(fulfillmentMethod, showDisabledState);
      },
      values: this.getExistingFulfillmentMethods(),
    });
  },

  hasPhoneNumberCustomerIdentifierAtTheBeginningOnKiosk() {
    return this.get('kioskDiningOptions').some(({ customerIdentifierOptions }) => {
      return customerIdentifierOptions.some(({ customerIdentifier, askAtBeginning }) => {
        return customerIdentifier === CustomerIdentifier.PhoneNumber && askAtBeginning;
      });
    });
  },

  hasIdentifiableKioskGuests() {
    return this.get('usesDelphi') || this.hasPhoneNumberCustomerIdentifierAtTheBeginningOnKiosk();
  },

  getAllI9nSchemas() {
    return Object.values(I9nSchemaBySystem).filter((i9nSchema) => {
      return this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getCreatableI9nSchemas() {
    const posI9nSchema = this.getPosI9nSchema();
    return Object.values(I9nSchemaBySystem).filter((i9nSchema) => {
      if (i9nSchema.isDeprecated) {
        return false;
      }
      // Don't show brand-specific integrations to non-Bite users so we don't leak that
      // (a) we build those
      // (b) who we build those for
      if (!app.sessionUser.isBite() && i9nSchema.isBrandSpecific) {
        return false;
      }

      if (i9nSchema.type === 'pos') {
        if (posI9nSchema) {
          if (this.get('multiVendorSupport') === LocationMultiVendorSupport.Enabled) {
            return true; // We don't care which systems are going to be mixed and matched
          }
          return false;
        }
        return true; // it's our first pos integration
      }
      if (i9nSchema.type === 'kiosk-payment') {
        if (!OrderChannelHelper.isKiosk(this.get('orderChannel'))) {
          return false;
        }

        const paymentI9nSchema = this.getKioskPaymentI9nSchema();
        if (paymentI9nSchema) {
          if (
            i9nSchema.system === IntegrationSystem.StripeTerminal &&
            this.get('multiPaymentSupport') === LocationMultiPaymentSupport.Enabled
          ) {
            return true;
          }
          return false;
        }
        return true; // it's our first payment integration
      }
      if (i9nSchema.type === 'ecomm-payment') {
        const ecommI9nSchema = this.getEcommPaymentI9nSchema();
        if (ecommI9nSchema) {
          if (
            i9nSchema.system === IntegrationSystem.Stripe &&
            this.get('multiPaymentSupport') === LocationMultiPaymentSupport.Enabled
          ) {
            return true;
          }
          return false;
        }
        return true; // it's our first payment integration
      }
      if (
        (i9nSchema.type === 'loyalty' || i9nSchema.type === 'loyalty-earner') &&
        (this.getLoyaltyI9nSchema() || this.getLoyaltyEarnerI9nSchema())
      ) {
        return false;
      }
      if (
        i9nSchema.type === 'campus-card' ||
        i9nSchema.type === 'comp-card' ||
        i9nSchema.type === 'cash-machine' ||
        i9nSchema.type === 'sms-notifier' ||
        i9nSchema.type === 'table-tracker'
      ) {
        return !this.getAllI9nSchemas().some(({ type }) => type === i9nSchema.type);
      }
      if (i9nSchema.type === 'pos-data-source') {
        // Don't let non-Bite users see this option
        if (!app.sessionUser.isBite()) {
          return false;
        }
        return !this.getAllI9nSchemas().some(({ type }) => type === i9nSchema.type);
      }

      if (i9nSchema.type === 'supplementary-menu-source') {
        switch (i9nSchema.system) {
          case IntegrationSystem.Sprinkles:
            return app.org.isSprinkles();
          default:
            throw new Error(`unhandled supplementary menu source: ${i9nSchema.system}`);
        }
      }

      if (i9nSchema.type === 'marketing') {
        switch (i9nSchema.system) {
          case IntegrationSystem.Qualtrics: {
            const QUALTRICS_ORG_IDS = new Set([
              '67dc69dbc1240ae80eaaa06d', // Jollibee
              '6672fbc57dd52328ca63288e', // Portillo's
            ]);

            if (!QUALTRICS_ORG_IDS.has(app.org.id)) {
              return false;
            }
          }
        }
      }

      return !this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getLoyaltyI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'loyalty' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getLoyaltyEarnerI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'loyalty-earner' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  usesFulfillmentNames() {
    return app.integrationList.models.some((i9n) => {
      const i9nSchema = i9n.getI9nSchema();

      if (i9nSchema.type !== 'fulfillment') {
        return false;
      }
      if (!this.hasIntegrationWithSystem(i9nSchema.system)) {
        return false;
      }

      if (
        i9nSchema.system === IntegrationSystem.QsrAutomations &&
        i9n.get('qsrAutomationsMode') === QsrAutomationsMode.PosWebhooks
      ) {
        return false;
      }

      return true;
    });
  },

  getPosI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'pos' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getPosI9nSchemas() {
    return Object.values(I9nSchemaBySystem).filter((i9nSchema) => {
      return i9nSchema.type === 'pos' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  /**
   * @returns {I9nSchema[]}
   */
  getStoredValueI9nSchemas() {
    return Object.values(I9nSchemaBySystem).filter((i9nSchema) => {
      return (
        (i9nSchema.type === 'stored-value' || i9nSchema.type === 'campus-card') &&
        this.hasIntegrationWithSystem(i9nSchema.system)
      );
    });
  },

  getKioskPaymentI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'kiosk-payment' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getEcommPaymentI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'ecomm-payment' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getCashMachineI9nSchema() {
    return Object.values(I9nSchemaBySystem).find((i9nSchema) => {
      return i9nSchema.type === 'cash-machine' && this.hasIntegrationWithSystem(i9nSchema.system);
    });
  },

  getFullPosI9n() {
    return _.first(this.getFullPosI9ns());
  },

  getFullPosI9ns() {
    return _.filter(app.integrationList.models, (i9n) => {
      return i9n.getI9nSchema().type === 'pos';
    });
  },

  getFullLoyaltyI9n() {
    return _.find(app.integrationList.models, (i9n) => {
      return i9n.getI9nSchema().type === 'loyalty';
    });
  },

  getFullI9nWithId(i9nId) {
    return _.find(app.integrationList.models, (i9n) => {
      return i9n.id === i9nId;
    });
  },

  syncsModelsWithType(modelType) {
    switch (modelType) {
      case ModelType.OpenHoursOverride:
      case ModelType.OpenHoursTimetable:
        if (this.get('googlePlace')?.id && app.locationSettings.get('syncOpenHoursFromGoogle')) {
          return true;
        }
    }

    return this.getFullPosI9ns().some((i9n) => {
      return i9n.syncsModelsWithType(modelType);
    });
  },

  supportsCompoundMenuItems() {
    const posI9nSchema = this.getPosI9nSchema();
    if (!posI9nSchema) {
      return false;
    }
    if (!posI9nSchema.supportsCompoundMenuItems) {
      return false;
    }
    const loyaltyI9nSchema = this.getLoyaltyI9nSchema();
    if (loyaltyI9nSchema && !loyaltyI9nSchema.supportsCompoundMenuItems) {
      return false;
    }
    return true;
  },

  isSimilar(location) {
    return this.get('orgId') === location.get('orgId');
  },

  isInDifferentTimezone() {
    return Time.guess() !== this.get('timezone');
  },

  getTimezoneAbbr() {
    return Time.zone(this.get('timezone')).abbr(Date.now());
  },

  usesTaxProfiles() {
    return (
      !this.getPosI9nSchemas().length ||
      this.getFullPosI9ns().some((i9n) => {
        return i9n.syncsModelsWithType(ModelType.TaxProfile);
      }) ||
      this.getPosI9nSchemas().some((posI9nSchema) => {
        return posI9nSchema.usesManualTaxProfiles;
      }) ||
      app.vendorList.isSomeVendorNonintegrated()
    );
  },

  supportsMultipleVendors() {
    return !!this.get('multiVendorSupport');
  },

  supportsPayingWithCash() {
    return _.all(this.getFullPosI9ns(), (i9n) => {
      return i9n.supportsPayingWithCash();
    });
  },

  supportsTips() {
    const posI9nsSupportTips = _.all(this.getFullPosI9ns(), (i9n) => {
      return i9n.supportsTips();
    });
    return posI9nsSupportTips && !this.hasMultiPaymentSupport();
  },

  supportsManualDiscounts() {
    const posI9nSchema = this.getPosI9nSchema();
    return posI9nSchema && posI9nSchema.supportsApplyingDiscounts;
  },

  hasMultiPaymentSupport() {
    return this.get('multiPaymentSupport') === LocationMultiPaymentSupport.Enabled;
  },

  getCalorieSuffix() {
    const hasSuffix = this.hasStr('calorieSuffix');
    const suffix = hasSuffix ? this.get('calorieSuffix') : 'Cals';
    return suffix;
  },

  matchesQuery(query /* optDisplayName */) {
    if (!query) {
      return true;
    }

    const specialTokens = query
      .toLowerCase()
      .split(' ')
      .filter((token) => {
        return token.charAt(0) === ':';
      });
    const matchesAllTokens = specialTokens.every((token) => {
      switch (token) {
        case ':canary':
          return this.isCanary() || this.isGcnCanary() || this.isGarconCanary();
        case ':lab':
          return this.isLab();
        case ':pilot':
          return this.isPilot();
        case ':vitrine':
          return this.get('useVitrine');
        case ':standalone':
          return !this.getPosI9nSchema();
      }

      return Object.values(I9nSchemaBySystem)
        .filter((i9nSchema) => {
          return !!i9nSchema.searchTokens.find((searchToken) => `:${searchToken}` === token);
        })
        .some((i9nSchema) => {
          return this.hasIntegrationWithSystem(i9nSchema.system);
        });
    });
    if (!matchesAllTokens) {
      return false;
    }

    const tokenlessQuery = query
      .split(' ')
      .filter((token) => {
        return token.charAt(0) !== ':';
      })
      .join(' ');

    return app.AbstractModel.prototype.matchesQuery.apply(this, [tokenlessQuery]);
  },
});
